import React, { SetStateAction, Dispatch, useState } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  DatePicker,
  OverlayTrigger,
  Tooltip
} from 'react-angle-dashboard-components'
import ReactToPrint from 'react-to-print'
import { FormLabel, FormControl } from '../styles'
import { FormData } from '../../../interfaces/eligibility'
import styled from 'styled-components'

interface SearchFormProps {
  showPrintBtn: boolean
  setQueryData: Dispatch<SetStateAction<any>>
  setNewQuery: () => void
  formData: FormData
  setFormData: Dispatch<SetStateAction<FormData>>

  printRef: any
  onBeforePrint: () => Promise<unknown>
  onAfterPrint: () => void
}

const HelpIcon = styled.i`
  color: var(--gray-400);
  height: 10px;
  width: 10px;
  position: relative;
  top: 0px;
  left: 4px;
`

export const SearchForm: React.FC<SearchFormProps> = ({
  showPrintBtn,
  setNewQuery,
  formData,
  setFormData,
  setQueryData,
  printRef,
  onBeforePrint,
  onAfterPrint
}) => {
  const [memberIdError, setMemberIdError] = useState(false)

  const onSubmit = (e: any) => {
    e.preventDefault()

    // set query to be enabled & unset the message
    setNewQuery()

    const { firstName, lastName, memberId, dateOfBirth, dateOfService } =
      formData

    // todo format query
    setQueryData({
      firstName,
      lastName,
      memberId,
      dateOfBirth,
      dateOfService
    })
  }

  return (
    <div>
      <Form onSubmit={onSubmit}>
        <Row>
          <Col sm={6} xs={12}>
            <Form.Group>
              <FormLabel>First Name</FormLabel>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip
                    data-testid="about-tooltip"
                    id="reconsideration tooltip"
                  >
                    Please use the exact spelling of the member's name,
                    including special characters and spaces
                  </Tooltip>
                }
              >
                <HelpIcon
                  data-testid="about-tooltip-trigger"
                  className="icon icon-question-mark fs-12"
                />
              </OverlayTrigger>
              <FormControl
                required
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e: any) =>
                  setFormData((prevState: any) => ({
                    ...prevState,
                    firstName: e.target.value
                  }))
                }
              />
            </Form.Group>
          </Col>
          <Col sm={6} xs={12}>
            <Form.Group>
              <FormLabel>Last Name</FormLabel>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip
                    data-testid="about-tooltip"
                    id="reconsideration tooltip"
                  >
                    Please use the exact spelling of the member's name,
                    including special characters and spaces
                  </Tooltip>
                }
              >
                <HelpIcon
                  data-testid="about-tooltip-trigger"
                  className="icon icon-question-mark fs-12"
                />
              </OverlayTrigger>
              <FormControl
                required
                placeholder="Last Name"
                value={formData.lastName}
                onChange={(e: any) =>
                  setFormData((prevState: any) => ({
                    ...prevState,
                    lastName: e.target.value
                  }))
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={6} xs={12}>
            <Form.Group className="pb-3">
              <FormLabel className="pb-1">Member's Date of Birth</FormLabel>
              <DatePicker
                required
                value={formData.dateOfBirth}
                onChange={(date) =>
                  setFormData((prevState: any) => {
                    return {
                      ...prevState,
                      dateOfBirth: date
                    }
                  })
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={6} xs={12}>
            <Form.Group className="pb-3">
              <FormLabel>Angle Member ID</FormLabel>
              <InputGroup>
                <InputGroup.Text>ANG</InputGroup.Text>
                <FormControl
                  placeholder="Please enter the member ID number"
                  required
                  maxLength={9}
                  value={formData.memberId}
                  isInvalid={memberIdError}
                  onChange={(e: any) => {
                    setMemberIdError(
                      e.target.value.toLowerCase().includes('ang')
                    )
                    setFormData((prevState: any) => ({
                      ...prevState,
                      memberId: e.target.value
                    }))
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col sm={6} xs={12}>
            <Form.Group className="pb-3">
              <FormLabel className="pb-1">
                Retrospective Date of Service (Optional)
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip
                      data-testid="about-tooltip"
                      id="reconsideration tooltip"
                    >
                      Select the date of service you'd like to query
                      eligibility, benefits, and coverage for. The deductible
                      and max out-of-pocket results will not be reflective of
                      this date. If unaltered, results will reflect today's
                      date. This date can only be current or retrospective, as
                      future benefits changes are unpredictable.
                    </Tooltip>
                  }
                >
                  <HelpIcon
                    data-testid="about-tooltip-trigger"
                    className="icon icon-question-mark fs-12"
                  />
                </OverlayTrigger>
              </FormLabel>
              <DatePicker
                value={formData.dateOfService}
                onChange={(date) =>
                  setFormData((prevState: any) => {
                    return {
                      ...prevState,
                      dateOfService: date
                    }
                  })
                }
                isOptional
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="py-5">
          <Button className="py-2 mr-3" type="submit">
            Search
          </Button>
          {showPrintBtn && (
            <ReactToPrint
              trigger={() => (
                <Button className="py-2" variant="outline-primary">
                  Print Results
                </Button>
              )}
              content={() => printRef.current}
              onBeforeGetContent={onBeforePrint}
              onAfterPrint={onAfterPrint}
            />
          )}
        </div>
      </Form>
    </div>
  )
}
